import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, mobile as fmtMobile } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';

interface AdminRow {
  id: string;
  displayName: string | null;
  mobile: string;
  email: string | null;
  isBlocked: boolean;
  isSuperAdmin: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  adminRole: { id: string; name: string } | null;
}

interface Role {
  id: string;
  name: string;
}

/** A password the server generated, shown to the Master Admin once. */
interface Issued {
  title: string;
  email: string;
  temporaryPassword: string;
}

export function AdminsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', mobile: '', roleId: '' });
  const [issued, setIssued] = useState<Issued | null>(null);
  const [removing, setRemoving] = useState<AdminRow | null>(null);

  const admins = useQuery({ queryKey: ['admin-admins'], queryFn: () => api.get<AdminRow[]>('/admin/admins') });
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: () => api.get<Role[]>('/admin/roles') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-admins'] });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ user: { email: string }; temporaryPassword: string }>('/master/admins', {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        roleId: form.roleId || null,
      }),
    onSuccess: (r) => {
      setCreating(false);
      setForm({ name: '', email: '', mobile: '', roleId: '' });
      setIssued({ title: 'Admin created', email: r.user.email, temporaryPassword: r.temporaryPassword });
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const reset = useMutation({
    mutationFn: (a: AdminRow) =>
      api.post<{ temporaryPassword: string }>(`/master/admins/${a.id}/reset-password`, {}).then((r) => ({ a, r })),
    onSuccess: ({ a, r }) => {
      setIssued({ title: 'Password reset', email: a.email ?? '', temporaryPassword: r.temporaryPassword });
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const assign = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      api.put(`/admin/users/${userId}/admin-role`, { roleId }),
    onSuccess: () => {
      toast('Role changed. It applies from their next action.', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: (a: AdminRow) => api.del(`/master/admins/${a.id}`),
    onSuccess: () => {
      toast('Admin access removed and their sessions ended', 'success');
      setRemoving(null);
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const valid =
    form.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && /^[6-9]\d{9}$/.test(form.mobile.trim());

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      <Notice tone="info" title="Admins sign in to the admin console, not here">
        An admin you create signs in at the admin console with their email and the temporary password. Only a Master
        Admin can sign in to this site. A password is shown once, when it is created or reset; it is stored only as a
        hash and cannot be shown again.
      </Notice>

      <Card flush>
        <CardHead
          title="Admins"
          sub="Everyone who can sign in to the admin console"
          action={
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              Create admin
            </Button>
          }
        />
        {admins.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 160 }} />
          </div>
        ) : !admins.data?.length ? (
          <Empty icon="☖" title="No admins" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Last signed in</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="cell-strong">
                        <Link to={`/users/${a.id}`}>{a.displayName ?? fmtMobile(a.mobile)}</Link>
                        {a.isBlocked && (
                          <span style={{ marginLeft: 7 }}>
                            <Badge tone="red">Blocked</Badge>
                          </span>
                        )}
                      </div>
                      <div className="cell-muted">{a.email ?? '—'}</div>
                      <div className="cell-muted">{fmtMobile(a.mobile)}</div>
                    </td>
                    <td className="cell-muted">{a.lastLoginAt ? date(a.lastLoginAt) : 'never'}</td>
                    <td>
                      {a.isSuperAdmin ? (
                        <Badge tone="purple">Master Admin</Badge>
                      ) : (
                        <Select
                          value={a.adminRole?.id ?? ''}
                          placeholder="No role · full access"
                          onChange={(e) => assign.mutate({ userId: a.id, roleId: e.target.value || null })}
                          options={(roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
                        />
                      )}
                    </td>
                    <td>
                      {!a.isSuperAdmin && (
                        <div className="cell-actions">
                          <Button size="sm" loading={reset.isPending && reset.variables?.id === a.id} onClick={() => reset.mutate(a)}>
                            Reset password
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setRemoving(a)}>
                            Remove
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <Modal
          title="Create an admin"
          sub="They sign in to the admin console with this email and a temporary password you are shown next."
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
              <Button variant="primary" loading={create.isPending} disabled={!valid} onClick={() => create.mutate()}>
                Create admin
              </Button>
            </>
          }
        >
          <div className="stack">
            <Field label="Name">
              <Input value={form.name} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email" hint="What they sign in with.">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Mobile" hint="Ten digits. If an account already has this number, it is made an admin.">
              <Input
                inputMode="numeric"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              />
            </Field>
            <Field label="Role" hint="What they may do. With no role an admin can do everything.">
              <Select
                value={form.roleId}
                placeholder="No role · full access"
                onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                options={(roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
              />
            </Field>
          </div>
        </Modal>
      )}

      {issued && (
        <Modal
          title={issued.title}
          sub="Copy this now and pass it on yourself. It will not be shown again."
          onClose={() => setIssued(null)}
          footer={
            <>
              <Button
                onClick={() => {
                  void navigator.clipboard.writeText(issued.temporaryPassword);
                  toast('Password copied', 'success');
                }}
              >
                Copy password
              </Button>
              <Button variant="primary" onClick={() => setIssued(null)}>
                Done
              </Button>
            </>
          }
        >
          <div className="kv">
            <span className="kv__k">Email</span>
            <span className="kv__v">{issued.email}</span>
            <span className="kv__k">Temporary password</span>
            <span className="kv__v cell-mono" style={{ fontSize: 16, userSelect: 'all' }}>
              {issued.temporaryPassword}
            </span>
          </div>
        </Modal>
      )}

      {removing && (
        <Modal
          title="Remove admin access"
          sub={`${removing.displayName ?? removing.email ?? fmtMobile(removing.mobile)} will be signed out now and can no longer sign in to the admin console. Their ordinary account stays.`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <Button onClick={() => setRemoving(null)}>Cancel</Button>
              <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(removing)}>
                Remove access
              </Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>This is recorded in the audit trail.</p>
        </Modal>
      )}
    </div>
  );
}
